import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import CustomSelect, { type SelectOption } from '../components/CustomSelect'
import FormInput from '../components/FormInput'
import FormTextarea from '../components/FormTextarea'
import FocusShield from '../components/FocusShield'
import { useKeyboardInset } from '../hooks/useKeyboardInset'
import { useNoDocScrollPush } from '../hooks/useNoDocScrollPush'
import { toastUtil } from '../utils/toast'
import { inquiryApi, type InquiryCategory } from '../api/inquiry'
import AttachPhotoRow, { type AttachedPhoto } from '../components/AttachPhotoRow'

const MAX_SUBJECT = 100
/** 넘으면 더 안 쳐진다. 숫자는 안 보여준다 — 거의 닿을 일이 없다. */
const MAX_BODY = 5000

/**
 * 하단 버튼 덩어리의 높이 — 버튼 55 + 위 여백 20 + 아래 여백 32.
 * 버튼을 바닥에 띄워 두므로, 스크롤 칸이 그만큼을 비워야 마지막 입력칸이 안 가린다.
 */
const BUTTON_BLOCK = 107

/**
 * 고객센터.
 *
 * 들어오자마자 쓸 수 있게 작성 폼이 첫 화면이다. 지난 문의는 앱바 오른쪽 '문의 내역'으로 간다.
 * 예전에는 환경설정에서 바텀시트를 올려 이메일 주소 한 줄을 보여주는 게 전부였다.
 *
 * 입력칸은 앱에 이미 있는 것들을 쓴다 — CustomSelect, FormInput, FormTextarea.
 * 새로 그리면 다른 화면과 모서리·높이·글자 크기가 어긋난다.
 *
 * 버튼과 키보드
 * ------------
 * 버튼은 앱 표준 자리 — 화면 바닥, px-20px, 아래 32px. 키보드가 올라와도
 * **움직이지 않는다**. 그래서 흐름에 두지 않고 바닥에 띄워 놓았다(absolute).
 * 흐름에 두면 위 칸이 줄어들 때 같이 따라 올라간다.
 *
 * 움직이는 건 스크롤 칸 하나뿐이다.
 *  · 평소      아래를 BUTTON_BLOCK 만큼 비운다 — 버튼에 가리지 않게
 *  · 키보드 때 아래를 키보드 높이만큼 비운다 — 입력칸이 키보드 바로 위에 선다
 *
 * 문서는 아예 못 밀게 막는다(useNoDocScrollPush). App.tsx 가 웹뷰 축소를 꺼둬서
 * (실기기 20회 측정 — 창 크기가 바뀌면 한 프레임 잘못된 값이 와서 크게 밀린다)
 * iOS 웹킷이 커서를 보이려고 문서를 통째로 올리는데, 그게 '화면이 너무 올라가는'
 * 것의 정체다. 막아 두고 스크롤 칸 안에서만 움직이게 한다.
 */
const Support: React.FC = () => {
  const navigate = useNavigate()
  const keyboard = useKeyboardInset()
  useNoDocScrollPush()

  const [categories, setCategories] = useState<InquiryCategory[]>([])
  const [category, setCategory] = useState<SelectOption | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [subjectFocused, setSubjectFocused] = useState(false)
  const [bodyFocused, setBodyFocused] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // 첨부 사진(선택). 고르는 즉시 올라가고 보낼 때는 주소만 싣는다
  const [photos, setPhotos] = useState<AttachedPhoto[]>([])
  const isSubmittingRef = useRef(false)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await inquiryApi.getInquiries()
        setCategories(res.categories)
      } catch {
        // 분류를 못 받으면 고를 수가 없다. 토스트는 띄우지 않는다 —
        // 들어오자마자 뜨면 무슨 일인지 알 수 없고, 보내기를 누르면 서버가 막는다.
      }
    }
    load()
  }, [])

  const options: SelectOption[] = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.label })),
    [categories]
  )

  // 셋 다 필수다. 하나라도 비면 보내기 버튼이 꺼진다.
  const isFormValid =
    !!category && subject.trim().length > 0 && body.trim().length > 0

  /** 버튼이 꺼져 있을 때 눌렀다면, 무엇이 빠졌는지 알려준다 */
  const handleDisabled = () => {
    if (!category) return toastUtil.error('카테고리를 선택해 주세요')
    if (subject.trim().length === 0) return toastUtil.error('제목을 입력해 주세요')
    if (body.trim().length === 0) return toastUtil.error('내용을 입력해 주세요')
  }

  const handleSubmit = async () => {
    if (!isFormValid) {
      handleDisabled()
      return
    }
    // 두 번 눌러 같은 문의가 두 건 접수되는 걸 막는다.
    // state 는 다음 렌더에야 반영되므로 ref 로 먼저 잠근다.
    if (isSubmittingRef.current) return
    // 아직 올라가는 사진이 있으면 기다리게 한다 — 그냥 보내면 그 사진이 빠진다
    if (photos.some((p) => p.url === undefined)) {
      toastUtil.info('사진을 올리는 중이에요. 잠시만 기다려 주세요')
      return
    }
    isSubmittingRef.current = true
    setIsSubmitting(true)

    try {
      await inquiryApi.createInquiry({
        category: category.value,
        subject: subject.trim(),
        body: body.trim(),
        photos: photos.map((p) => p.url).filter((u): u is string => !!u),
      })
      toastUtil.success('문의를 보냈어요')
      setSubject('')
      setBody('')
      setPhotos([])
      setCategory(null)
      navigate('/support/list')
    } catch {
      // 서버 문장은 apiClient 인터셉터가 토스트로 띄운다
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container relative flex-1 flex flex-col overflow-hidden">
        <AppBar
          title="고객센터"
          isBottomBorder
          rightElement={
            <button
              type="button"
              onClick={() => navigate('/support/list')}
              className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] pr-20px"
            >
              문의 내역
            </button>
          }
        />

        <div
          className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-20px"
          style={{
            /*
             * 일부러 transition 을 걸지 않는다.
             *
             * 키보드와 같은 시간 동안 부드럽게 움직이게 해봤는데, 아이폰에서는
             * 그게 '한 번 아래로 갔다 올라오는' 모션으로 보였다. WKWebView 는
             * 커서가 가는 순간 안쪽 스크롤 칸을 즉시 움직여 입력칸을 보여주는데,
             * 우리 여백은 그 뒤에 천천히 차오른다. 칸의 스크롤 가능한 높이가
             * 도중에 계속 바뀌니 브라우저가 스크롤 위치를 다시 잡고, 그 두 단계가
             * 눈에는 왕복으로 보인다.
             *
             * 즉시 적용하면 웹킷이 움직이기 전에 자리가 이미 정해져 있어
             * 한 번에 끝난다. 키보드가 그 위를 덮으며 올라올 뿐이다.
             */
            paddingBottom: keyboard.padding > 0 ? keyboard.padding : BUTTON_BLOCK,
          }}
        >
          {/*
            relative z-[2] — 펼친 목록이 아래 제목칸의 FocusShield(z-[1])보다 위에 떠야 한다.
            react-select 목록도 z-index 1이라, 같은 높이면 DOM 뒤에 오는 투명 층이 이겨서
            항목을 눌러도 제목칸으로 포커스만 가고 선택이 안 됐다.
          */}
          <div className="relative z-[2] mt-20px mb-16px">
            <label
              htmlFor="inquiry-category"
              className="block font-pretendard font-bold text-16px leading-20px text-brown-80%-main mb-10px"
            >
              카테고리를 선택해주세요
            </label>
            {/*
              기본값을 비워 둔다. 첫 항목이 미리 골라져 있으면 다들 그대로 두고
              보내서 분류가 통계로 쓸모없어진다.
            */}
            <CustomSelect
              options={options}
              value={category}
              onChange={setCategory}
              placeholder="분류를 선택해 주세요"
              boxShadow="none"
            />
          </div>

          {/* 투명한 층을 덮어 입력칸을 직접 누르지 않게 한다 — FocusShield 주석 참고 */}
          <div className="relative">
            <FormInput
              label="제목"
              name="inquirySubject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setSubjectFocused(true)}
              onBlur={() => setSubjectFocused(false)}
              placeholder="어떤 내용인지 한 줄로 알려주세요"
              maxLength={MAX_SUBJECT}
            />
            <FocusShield
              targetId="inquirySubject"
              active={!(subjectFocused && subject.length > 0)}
            />
          </div>

          {/* 사진 첨부(선택) — 제목과 내용 사이. 기록 화면과 같은 사진 칸 */}
          <div className="mb-20px">
            <p className="font-pretendard font-bold text-16px leading-20px text-brown-80%-main mb-10px">
              사진
            </p>
            <AttachPhotoRow photos={photos} onChange={setPhotos} max={5} />
          </div>

          <label
            htmlFor="inquiryBody"
            className="block font-pretendard font-bold text-16px leading-20px text-brown-80%-main mb-10px"
          >
            내용
          </label>
          <div className="relative">
            <FormTextarea
              name="inquiryBody"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => setBodyFocused(true)}
              onBlur={() => setBodyFocused(false)}
              placeholder="자세히 적어주시면 더 빠르게 도와드릴 수 있어요"
              maxLength={MAX_BODY}
              height="h-170px"
            />
            <FocusShield
              targetId="inquiryBody"
              active={!(bodyFocused && body.length > 0)}
            />
          </div>
        </div>

        {/* 하단 고정 버튼 — 바닥에 띄워 둔다. 키보드가 올라와도 제자리 */}
        <div className="absolute left-0 right-0 bottom-0 px-20px pb-32px">
          <SubmitButton
            text="문의하기"
            onSubmit={handleSubmit}
            onDisabled={handleDisabled}
            disabled={!isFormValid || isSubmitting}
            loading={isSubmitting}
            addClassName="mt-20px"
          />
        </div>
      </div>
    </div>
  )
}

export default Support
